import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiOutlineBell } from "react-icons/hi";
import {
  NOTIFICATION_PAGE_SIZE,
  NOTIFICATION_RECEIVED_EVENT,
  NOTIFICATION_SOCKET_REGISTER_EVENT,
  NOTIFICATION_TOAST_CLICK_EVENT,
  getUnreadNotificationCount,
  listNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from "../services/notificationApi";
import {
  appendNotifications,
  formatBadgeCount,
  formatRelativeTime,
  getInternalNotificationLink,
  getNotificationStyle,
  getNotificationText,
} from "../utils/notificationUtils";

const POLL_INTERVAL_MS = 60 * 1000;

const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const [error, setError] = useState("");
  const [now, setNow] = useState(() => new Date());

  const containerRef = useRef(null);
  const isOpenRef = useRef(false);
  // กัน response ของ request เก่ามาทับรายการที่เพิ่งโหลดใหม่
  const listRequestIdRef = useRef(0);

  useEffect(() => {
    isOpenRef.current = isOpen;
  }, [isOpen]);

  const refreshUnreadCount = useCallback(async () => {
    if (!localStorage.getItem("token")) return;
    try {
      setUnreadCount(await getUnreadNotificationCount());
    } catch (err) {
      console.error("❌ Error fetching unread notification count:", err);
    }
  }, []);

  // โหลดหน้าแรกใหม่และแทนที่รายการทั้งชุด (แจ้งเตือนที่ถูก withdraw จะหายไปเอง)
  // คืนรายการที่โหลดได้ หรือ null ถ้าโหลดไม่สำเร็จ / มี request ใหม่กว่ามาแทน
  const reloadNotifications = useCallback(async () => {
    if (!localStorage.getItem("token")) return null;
    const requestId = ++listRequestIdRef.current;
    setLoading(true);
    setError("");
    try {
      const result = await listNotifications({ page: 1 });
      if (requestId !== listRequestIdRef.current) return null;
      setNotifications(result.data);
      setUnreadCount(result.unreadCount);
      setPage(1);
      setTotalPages(result.pagination.totalPages || 0);
      setNow(new Date());
      return result.data;
    } catch (err) {
      if (requestId !== listRequestIdRef.current) return null;
      console.error("❌ Error fetching notifications:", err);
      setError("โหลดการแจ้งเตือนไม่สำเร็จ");
      return null;
    } finally {
      if (requestId === listRequestIdRef.current) setLoading(false);
    }
  }, []);

  const loadMore = async () => {
    const requestId = listRequestIdRef.current;
    const nextPage = page + 1;
    setLoadingMore(true);
    try {
      const result = await listNotifications({
        page: nextPage,
        limit: NOTIFICATION_PAGE_SIZE,
      });
      if (requestId !== listRequestIdRef.current) return;
      setNotifications((prev) => appendNotifications(prev, result.data));
      setUnreadCount(result.unreadCount);
      setPage(nextPage);
      setTotalPages(result.pagination.totalPages || 0);
    } catch (err) {
      console.error("❌ Error loading more notifications:", err);
      setError("โหลดเพิ่มเติมไม่สำเร็จ");
    } finally {
      setLoadingMore(false);
    }
  };

  // badge ตอนเปิดหน้า + poll ทุก 60 วินาทีเผื่อ socket หลุด (ข้ามตอนแท็บซ่อนอยู่)
  useEffect(() => {
    refreshUnreadCount();
    window.dispatchEvent(new Event(NOTIFICATION_SOCKET_REGISTER_EVENT));

    const intervalId = setInterval(() => {
      if (document.visibilityState !== "visible") return;
      refreshUnreadCount();
      setNow(new Date());
    }, POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") refreshUnreadCount();
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshUnreadCount]);

  // แจ้งเตือน realtime จาก socket listener ใน App.js (toast แสดงที่นั่นแล้ว)
  useEffect(() => {
    const handleReceived = () => {
      if (isOpenRef.current) {
        reloadNotifications();
      } else {
        refreshUnreadCount();
      }
    };
    window.addEventListener(NOTIFICATION_RECEIVED_EVENT, handleReceived);
    return () =>
      window.removeEventListener(NOTIFICATION_RECEIVED_EVENT, handleReceived);
  }, [refreshUnreadCount, reloadNotifications]);

  // ปิด dropdown เมื่อคลิกข้างนอกหรือกด Esc
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) reloadNotifications();
  };

  // optimistic: อัปเดต UI ก่อน แล้วค่อยยิง API (ไม่ต้องรอผลก่อนเปิดหน้า)
  const markItemRead = useCallback(
    async (notification) => {
      if (notification.isRead) return;

      const readAt = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) =>
          n._id === notification._id ? { ...n, isRead: true, readAt } : n
        )
      );
      setUnreadCount((prev) => Math.max(prev - 1, 0));

      try {
        await markNotificationAsRead(notification._id);
      } catch (err) {
        console.error("❌ Error marking notification as read:", err);
        if (err?.response?.status === 404) {
          // ถูกลบไปแล้ว (เช่น timesheet ถูก withdraw) เอาออกจากรายการ
          setNotifications((prev) =>
            prev.filter((n) => n._id !== notification._id)
          );
        } else {
          setNotifications((prev) =>
            prev.map((n) =>
              n._id === notification._id ? { ...n, isRead: false, readAt: null } : n
            )
          );
        }
        refreshUnreadCount();
      }
    },
    [refreshUnreadCount]
  );

  // คลิกแจ้งเตือน: mark read และถ้ามี link ภายในแอปให้เปิดหน้านั้น (แจ้งเตือนเก่าไม่มี link)
  const openNotification = useCallback(
    (notification) => {
      markItemRead(notification);
      const link = getInternalNotificationLink(notification);
      if (!link) return;
      setIsOpen(false);
      navigate(link);
    },
    [markItemRead, navigate]
  );

  // คลิก toast: payload ของ socket ไม่มี link จึงโหลดรายการใหม่แล้วเปิดรายการล่าสุด
  useEffect(() => {
    const handleToastClick = async () => {
      const latest = (await reloadNotifications())?.[0];
      if (latest) openNotification(latest);
    };
    window.addEventListener(NOTIFICATION_TOAST_CLICK_EVENT, handleToastClick);
    return () =>
      window.removeEventListener(NOTIFICATION_TOAST_CLICK_EVENT, handleToastClick);
  }, [openNotification, reloadNotifications]);

  const handleMarkAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await markAllNotificationsAsRead();
      const readAt = new Date().toISOString();
      setNotifications((prev) =>
        prev.map((n) => (n.isRead ? n : { ...n, isRead: true, readAt }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error("❌ Error marking all notifications as read:", err);
      setError("อ่านทั้งหมดไม่สำเร็จ ลองใหม่อีกครั้ง");
    } finally {
      setMarkingAll(false);
    }
  };

  const hasMore = page < totalPages;

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        onClick={toggleDropdown}
        className="relative p-1 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
        aria-label={`การแจ้งเตือน${unreadCount > 0 ? ` (${unreadCount} ยังไม่อ่าน)` : ""}`}
        aria-expanded={isOpen}
      >
        <HiOutlineBell size={24} />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-2 min-w-[1.25rem] h-5 px-1 bg-red-500 text-white text-[11px] leading-none font-semibold rounded-full flex items-center justify-center">
            {formatBadgeCount(unreadCount)}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-3 w-[calc(100vw-2rem)] max-w-sm sm:w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 shadow-lg rounded-lg z-50 transition-colors duration-300">
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <span className="font-semibold text-gray-700 dark:text-gray-100">
              การแจ้งเตือน
            </span>
            {unreadCount > 0 && (
              <button
                type="button"
                onClick={handleMarkAllAsRead}
                disabled={markingAll}
                className="text-xs text-blue-500 hover:underline disabled:opacity-50 disabled:no-underline"
              >
                {markingAll ? "กำลังอัปเดต..." : "อ่านทั้งหมด"}
              </button>
            )}
          </div>

          {error && (
            <div className="px-4 py-2 text-xs text-red-600 bg-red-50 dark:bg-red-900/30 dark:text-red-300 flex justify-between items-center gap-2">
              <span>{error}</span>
              <button
                type="button"
                onClick={reloadNotifications}
                className="underline shrink-0"
              >
                ลองใหม่
              </button>
            </div>
          )}

          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                กำลังโหลด...
              </p>
            ) : notifications.length > 0 ? (
              <>
                {notifications.map((n) => {
                  const { icon, accent } = getNotificationStyle(n);
                  const { title, message } = getNotificationText(n);
                  return (
                    <button
                      type="button"
                      key={n._id}
                      onClick={() => openNotification(n)}
                      className={`w-full text-left flex gap-3 px-4 py-3 text-sm border-b border-gray-100 dark:border-gray-700 transition-colors cursor-pointer ${
                        n.isRead
                          ? "bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700/60"
                          : "bg-blue-50/60 dark:bg-gray-700/50 hover:bg-blue-50 dark:hover:bg-gray-700"
                      }`}
                    >
                      <span className="text-lg leading-none mt-0.5" aria-hidden="true">
                        {icon}
                      </span>
                      <span className="flex-1 min-w-0">
                        <span
                          className={`block break-words ${
                            n.isRead
                              ? "text-gray-600 dark:text-gray-300"
                              : "font-semibold text-gray-800 dark:text-gray-100"
                          }`}
                        >
                          {title}
                        </span>
                        {message && (
                          <span className="block mt-0.5 break-words text-gray-500 dark:text-gray-400">
                            {message}
                          </span>
                        )}
                        <span className="block mt-1 text-xs text-gray-400 dark:text-gray-500">
                          {formatRelativeTime(n.createdAt, now)}
                        </span>
                      </span>
                      {!n.isRead && (
                        <span
                          className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${accent}`}
                          aria-label="ยังไม่อ่าน"
                        />
                      )}
                    </button>
                  );
                })}
                {hasMore && (
                  <button
                    type="button"
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="w-full py-2.5 text-sm text-blue-500 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
                  >
                    {loadingMore ? "กำลังโหลด..." : "ดูเพิ่มเติม"}
                  </button>
                )}
              </>
            ) : (
              !error && (
                <p className="p-4 text-sm text-gray-500 dark:text-gray-400 text-center">
                  ไม่มีการแจ้งเตือน
                </p>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
